/**
 * useVersionWatcher — silently detect new production deploys and force a
 * one-shot hard reload so every user picks up the new JS bundle.
 *
 * Problem this solves:
 *   After each deploy, browsers + Service Worker often serve the OLD
 *   cached `main.<hash>.js` even though `/api/system/version` reports a
 *   newer buildDate. Users hit ReferenceErrors when their old JS calls
 *   into a backend that has moved on.
 *
 * How it works:
 *   1. On mount, poll `/api/system/version` once.
 *   2. Remember `buildDate` in memory.
 *   3. Every 60s (and on `visibilitychange` → tab refocus), poll again.
 *   4. If buildDate is NEWER than what we remembered → wait 2s, then
 *      do `location.reload()` once per session (sessionStorage guard).
 *
 * The 2-second pause is intentional — it lets in-flight requests finish
 * and shows the user a brief, non-intrusive toast so it doesn't feel
 * like a crash.
 */
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { API } from "../utils";

const POLL_INTERVAL_MS = 60_000;
const RELOAD_FLAG = "__blk_version_reload_attempted__";

export default function useVersionWatcher() {
  const initialBuildRef = useRef(null);
  const reloadedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let intervalId = null;

    const fetchVersion = async () => {
      try {
        const r = await fetch(`${API}/system/version`, { cache: "no-store" });
        if (!r.ok) return;
        const data = await r.json();
        return data?.buildDate || null;
      } catch {
        return null;
      }
    };

    const checkForNewBuild = async () => {
      if (cancelled || reloadedRef.current) return;
      const current = await fetchVersion();
      if (!current) return;

      if (initialBuildRef.current == null) {
        initialBuildRef.current = current;
        return;
      }
      if (current !== initialBuildRef.current) {
        if (sessionStorage.getItem(RELOAD_FLAG)) return;
        sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
        reloadedRef.current = true;

        toast.success("A new version is live — refreshing for you…", { duration: 1800 });
        setTimeout(() => {
          // Unregister any stale service workers so we never re-cache an old shell
          if ("serviceWorker" in navigator) {
            navigator.serviceWorker.getRegistrations?.().then((regs) => {
              regs.forEach((r) => r.unregister().catch(() => {}));
            });
          }
          window.location.reload();
        }, 2000);
      }
    };

    // Initial fetch
    checkForNewBuild();
    // Poll every 60s
    intervalId = setInterval(checkForNewBuild, POLL_INTERVAL_MS);
    // Also re-check when the tab regains focus (catches users who left tab
    // open across a deploy window without polling).
    const onVisibility = () => { if (document.visibilityState === "visible") checkForNewBuild(); };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);
}
